import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate L2-5 del Barbaro (manuale V1, rr. 2054-2142).
 *
 * **16 mosse** disponibili per il level-up dei livelli 2-5.
 *
 * **Pattern critici**:
 *
 * - ***Istinti Primordiali*** (description-only): concede al PG un altro
 *   Istinto del Barbaro non ancora preso. Pattern trasversale (8+ casi su 12
 *   classi: *Allargare le Basi* Chierico, *Mille Risorse* Ladro, *Di Tutto un
 *   Po'* Bardo, *Formazione Eterogenea* Mago, *Bambole e Pendoli* Sciamano,
 *   *Istinti Primordiali* + *Ho Ancora Fame* Barbaro). Tracciato in **FI-31
 *   nuovo** (P3). Il gancio strutturale è `expandableInAdvanced: true` sul
 *   gruppo Istinti della `ClassDefinition` — primo uso del flag fra le 12
 *   classi DWR. La logica builder che onora il flag arriverà in Fase D
 *   (level-up).
 *
 * - ***Banda di Guerra*** (FI-8 stub description-only): gregario di **livello
 *   2** esplicito nel testo (5 persone, Costo «Soddisfare uno dei tuoi
 *   appetiti», Lealtà 2, 5 PF, +2 a un modificatore di Caratteristica + +1 a
 *   tutti gli altri). Coerente con *Compagno Animale* del Ranger, *Famiglio*
 *   del Mago, *Supporto Elementale* del Druido, *Richiama Cavalcatura* del
 *   Paladino, *Braccio Destro* chain del Warlock, *Animare Zombi* dello
 *   Sciamano. `prerequisites: ['barbaro.appetiti-erculei']` strutturato per
 *   facilitare il refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi.
 *
 * - ***Ho Ancora Fame*** (description-only): concede un altro appetito dalla
 *   lista di *Appetiti Erculei* (mossa iniziale auto con `choices` 6,
 *   `choiceCount: 2`). Pattern FI-31. `prerequisites:
 *   ['barbaro.appetiti-erculei']`.
 *
 * - ***Fame Insaziabile*** (description-only): regola runtime di mitigazione
 *   danno con `-1 continuato`. `prerequisites: ['barbaro.appetiti-erculei']`.
 *
 * - ***Fame per La Distruzione*** (multiclasse): tag `'multiclasse'`,
 *   description-only — il PG sceglie una mossa non multiclasse di
 *   Guerriero/Bardo/Ladro/Ranger/Sciamano. Pattern FI-17.
 *
 * **3 mosse** richiedono *Appetiti Erculei* (auto, prereq sempre soddisfatto):
 * Banda di Guerra, Ho Ancora Fame, Fame Insaziabile.
 *
 * **Nessuna mossa ha `MoveSchema.replaces` in questo bucket** (i `replaces` del
 * Barbaro sono tutti in L6-10: *Potenza Indomabile* sostituisce *Assalto
 * Irruento*).
 */

const ilMeglioDellaVita: Move = {
  id: 'barbaro.il-meglio-della-vita',
  name: 'Il Meglio della Vita',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando fai il *fine sessione*',
  description:
    'Se durante la stessa hai sconfitto i tuoi nemici, li hai inseguiti mentre fuggivano, o hai sentito i lamenti dei loro cari, **segna un PE**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const istintiPrimordiali: Move = {
  id: 'barbaro.istinti-primordiali',
  name: 'Istinti Primordiali',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando prendi questa mossa',
  description:
    'Ottieni un Istinto del Barbaro che non hai già (*Avventura: Un Amante in Ogni Porto*, *Battaglia: Un Fascio di Muscoli*, o *Misticismo: Animale Totemico*).',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede un Istinto residuo dal gruppo `istinti` del
  // Barbaro (`startingChoiceGroups[1]`, `expandableInAdvanced: true`).
  grantsMoveChoice: { kind: 'from-group', groupId: 'istinti' },
};

const cosaStaiAspettando: Move = {
  id: 'barbaro.cosa-stai-aspettando',
  name: 'Cosa Stai Aspettando?',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando lanci una sfida ai tuoi nemici',
  description:
    'Tira+**COS**. Con 10+, essi ti trattano come la minaccia più importante di cui occuparsi, ignorano i tuoi compagni, e tu infliggi loro **+2 danni**. Con un 7-9, ottieni lo stesso effetto, ma solo alcuni dei nemici (i più deboli o i più avventati) cadono vittima della tua provocazione.',
  roll: {
    stat: 'costituzione',
    on10plus: {
      text: 'I nemici ti trattano come la minaccia primaria, ignorano i tuoi compagni, +2 danni contro di loro.',
    },
    on7to9: { text: 'Stesso effetto, ma solo alcuni nemici (i più deboli o avventati).' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const bandaDiGuerra: Move = {
  id: 'barbaro.banda-di-guerra',
  name: 'Banda di Guerra',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando usi la mossa *reclutare* per riformare la tua *Banda di Guerra*',
  description:
    "Hai radunato un manipolo di **cinque persone** che ti seguono e combattono per te. Consideralo come *un solo* **gregario collettivo**: i PF rappresentano il numero di membri attivi.\n\nQuando **usi la mossa *reclutare* per riformare la tua *Banda di Guerra***, con 10+, la Banda recupera tutti i suoi PF e puoi cambiare la Caratteristica per la quale ha il modificatore aumentato. Con 7-9, recupera la metà dei suoi PF massimi (arrotondati per eccesso). Con 6-, non recupera alcun PF e il GM introduce qualche complicazione.\n\nIl GM definisce l'effetto dell'equipaggiamento che fornisci loro e quanta «roba» possono portare (Carico narrativo, fiction-based).\n\n*Il Khan dei Khan* (L6-10) trasforma la Banda in gregario di livello 3 con 8 membri e mod +3.",
  roll: {
    stat: 'costituzione',
    on10plus: {
      text: 'La Banda recupera tutti i suoi PF e puoi cambiare la Caratteristica per la quale ha il modificatore aumentato.',
    },
    on7to9: { text: 'La Banda recupera la metà dei suoi PF massimi (arrotondati per eccesso).' },
    on6minus: { text: 'Nessun recupero, e il GM introduce qualche complicazione.' },
  },
  tags: [],
  prerequisites: ['barbaro.appetiti-erculei'],
  replaces: [],
  // D8b — FI-8f: mossa-source di Follower (Banda di Guerra collective).
  followerTemplateId: 'barbaro-banda-di-guerra',
  choiceGroups: [
    {
      id: 'modificatore',
      label: 'Modificatore a +2',
      description: 'Scegli la Caratteristica che la Banda ha a +2 (le altre restano +1).',
      pickCount: 1,
      options: [
        { id: 'forza', label: 'Forza' },
        { id: 'destrezza', label: 'Destrezza' },
        { id: 'costituzione', label: 'Costituzione' },
        { id: 'intelligenza', label: 'Intelligenza' },
        { id: 'saggezza', label: 'Saggezza' },
        { id: 'carisma', label: 'Carisma' },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'nome-banda',
      label: 'Nome della Banda',
      placeholder: 'es. I Lupi di Vetro, La Mandria di Ferro, Gli Ultimi…',
      required: true,
      multiline: false,
    },
  ],
};

const hoAncoraFame: Move = {
  id: 'barbaro.ho-ancora-fame',
  name: 'Ho Ancora Fame',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando prendi questa mossa',
  description:
    'Scegli un altro appetito dalla lista di *Appetiti Erculei* (distruzione/potere/piaceri/conquista/ricchezze/fama).',
  tags: [],
  prerequisites: ['barbaro.appetiti-erculei'],
  replaces: [],
  // FI-31 (D5): concede un appetito residuo dalle `choices` della
  // mossa-config sorgente `barbaro.appetiti-erculei` (6 appetiti, 2 già
  // scelti al L1 → 4 residui).
  grantsMoveChoice: { kind: 'from-choice-move', moveId: 'barbaro.appetiti-erculei' },
};

const fameInsaziabile: Move = {
  id: 'barbaro.fame-insaziabile',
  name: 'Fame Insaziabile',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando subisci danno',
  description:
    'Se scegli di prendere **-1 continuato** a tutti i tiri, non subisci tale danno. Se hai già la penalità derivante da questa mossa, non puoi prenderne una seconda per evitare il danno. La penalità permane finché non sazi uno dei tuoi appetiti.',
  tags: [],
  prerequisites: ['barbaro.appetiti-erculei'],
  replaces: [],
};

const famePerLaDistruzione: Move = {
  id: 'barbaro.fame-per-la-distruzione',
  name: 'Fame per La Distruzione',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando prendi questa mossa',
  description:
    'Prendi una mossa non multiclasse tra quelle del Guerriero, del Bardo, del Ladro, del Ranger o dello Sciamano.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool ristretto a 5 classi (manuale V1 r. ~1990).
  multiclass: {
    picks: {
      kind: 'fixed-classes',
      classes: ['guerriero', 'bardo', 'ladro', 'ranger', 'sciamano'],
    },
  },
};

const assaltoIrruento: Move = {
  id: 'barbaro.assalto-irruento',
  name: 'Assalto Irruento',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando *tagli e spacchi*',
  description:
    "Con 10+, se decidi di lasciare comunque che l'avversario risponda, oltre agli effetti aggiuntivi già previsti (in genere +1d6 danni), infliggi **+1d4 danni**.\n\nRicorda: la risposta del nemico può essere qualsiasi cosa, non per forza infliggerti danno!\n\nA livello 6-10 questa mossa è sostituita da *Potenza Indomabile* (+1d8 anziché +1d4).",
  tags: [],
  prerequisites: [],
  // Sostituita da `barbaro.potenza-indomabile` (L6-10, +1d8) via `MoveSchema.replaces`
  // sul lato del successor.
  replaces: [],
};

const ira: Move = {
  id: 'barbaro.ira',
  name: 'Ira',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando ti lasci pervadere dalla furia',
  description:
    "Entri in *Ira*. Tira+**COS**. Con 10+, ottieni **3 prese**. Con 7-9, prendi 2, ma subisci un -1 continuato a tutti i tiri per entrare in *Ira* finché non riposi una notte intera. Con 6-, ottieni comunque 1 presa, ma non puoi usare nuovamente questa mossa finché non riesci a goderti una notte di riposo ininterrotto.\n\nSpendi al massimo una presa alla volta per:\n\n- ottenere un **+1 al prossimo tiro** per una mossa che richieda di tirare+**FOR**, +**COS** o +**DES**;\n- infliggere **+1d6 danni** con un attacco andato a segno.\n\nMentre **sei in *Ira***, subisci un **-1 continuato** a qualsiasi mossa richieda di tirare+**INT**, +**SAG** o +**CAR**. Quando **hai finito le prese**, esci dall'*Ira*. Puoi anche decidere di spendere tutte le prese residue per uscire dall'*Ira*.",
  roll: {
    stat: 'costituzione',
    on10plus: { text: 'Ottieni 3 prese.' },
    on7to9: {
      text: 'Prendi 2, ma -1 continuato a tutti i tiri di *Ira* finché non riposi una notte intera.',
    },
    on6minus: { text: 'Ottieni 1 presa, ma non puoi usare di nuovo *Ira* finché non riposi.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sempreInMovimento: Move = {
  id: 'barbaro.sempre-in-movimento',
  name: 'Sempre in Movimento',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger:
    'Quando *sfidi il pericolo* causato da un movimento (lasciarti cadere da un ponte, superare di corsa una guardia armata, ecc.)',
  description: 'Hai **+1 al tiro**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sanaDiffidenza: Move = {
  id: 'barbaro.sana-diffidenza',
  name: 'Sana Diffidenza',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando la magia corrotta, controllata dai mortali, ti obbliga a *sfidare il pericolo*',
  description: 'Considera qualunque risultato di 6− come se fosse un 7–9.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sansone: Move = {
  id: 'barbaro.sansone',
  name: 'Sansone',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando sei in un qualunque stato di costrizione fisica o mentale',
  description:
    'Puoi subire una **Debilitazione** a tua scelta e liberarti immediatamente. Se hai già tutte le Debilitazioni segnate, non puoi usare questa mossa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const premonizioniDiSangue: Move = {
  id: 'barbaro.premonizioni-di-sangue',
  name: 'Premonizioni di Sangue',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger:
    'Quando sopravvivi a una dura battaglia, e ti ergi sulle spoglie dei tuoi nemici coperto di sangue',
  description:
    "Descrivi come ti senti. Il GM ti fornirà un'intuizione riguardo gli avversari che hai appena sconfitto (le loro vere motivazioni, i loro piani, chi li comanda realmente, ecc.).",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const occhioAiPuntiDeboli: Move = {
  id: 'barbaro.occhio-ai-punti-deboli',
  name: 'Occhio ai Punti Deboli',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando *discerni realtà*',
  description:
    "Aggiungi l'opzione «*Cosa qui è debole o vulnerabile?*» alla lista di domande che puoi fare.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const usurpatore: Move = {
  id: 'barbaro.usurpatore',
  name: 'Usurpatore',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando dimostri di essere più forte di una persona importante',
  description: 'Prendi **+1 al prossimo tiro** con i suoi seguaci, subalterni e sostenitori.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilMioAmorePerTe: Move = {
  id: 'barbaro.il-mio-amore-per-te-e-come-un-carro',
  name: 'Il Mio Amore per Te è come un Carro',
  type: 'advanced_2_5',
  classId: 'barbaro',
  trigger: 'Quando ti metti in mostra con una prova di forza',
  description:
    'Nomina uno dei presenti: lo hai impressionato e prendi **+1 al prossimo tiro di *parlamentare*** con lui.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const barbaroAdvanced2to5Moves: readonly Move[] = [
  ilMeglioDellaVita,
  istintiPrimordiali,
  cosaStaiAspettando,
  bandaDiGuerra,
  hoAncoraFame,
  fameInsaziabile,
  famePerLaDistruzione,
  assaltoIrruento,
  ira,
  sempreInMovimento,
  sanaDiffidenza,
  sansone,
  premonizioniDiSangue,
  occhioAiPuntiDeboli,
  usurpatore,
  ilMioAmorePerTe,
] as const;
